import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, BookOpen, HelpCircle, Settings, LogOut, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_sf-training-hub/artifacts/ftwj8azm_VIP%20plus%20logo.png";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/vipplus/login');
  };

  const isActive = (path) => {
    if (path === '/vipplus') return location.pathname === '/vipplus';
    return location.pathname.startsWith(path);
  };

  return (
    <nav 
      className="sticky top-0 z-50 w-full border-b bg-[#571925] text-white h-16 flex items-center"
      data-testid="navbar"
    >
      <div className="container mx-auto px-4 md:px-8 flex items-center justify-between h-full">
        {/* Logo and Title */}
        <Link to="/vipplus" className="flex items-center gap-3" data-testid="navbar-logo">
          <img 
            src={LOGO_URL} 
            alt="VIP Plus" 
            className="h-10 w-auto"
          />
          <div className="hidden md:block">
            <h1 className="text-lg font-semibold leading-tight">VIP Plus Academy</h1>
            <p className="text-xs text-white/70">Formation Salesforce</p>
          </div>
        </Link>

        {/* Navigation Links */}
        <div className="flex items-center gap-1 md:gap-2">
          <Link to="/vipplus">
            <Button 
              variant="ghost" 
              className={`text-white hover:bg-white/10 ${isActive('/vipplus') && location.pathname === '/vipplus' ? 'bg-white/20' : ''}`}
              data-testid="nav-home"
            >
              <Home className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Accueil</span>
            </Button>
          </Link>
          
          <Link to="/vipplus/feedback/suggestions">
            <Button 
              variant="ghost" 
              className={`text-white hover:bg-white/10 ${isActive('/vipplus/feedback') ? 'bg-white/20' : ''}`}
              data-testid="nav-support"
            >
              <HelpCircle className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Support</span>
            </Button>
          </Link>

          <Link to="/vipplus/admin">
            <Button 
              variant="ghost" 
              className={`text-white hover:bg-white/10 ${isActive('/vipplus/admin') ? 'bg-white/20' : ''}`}
              data-testid="nav-admin"
            >
              <Settings className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Admin</span>
            </Button>
          </Link>

          {/* User Menu */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="text-white hover:bg-white/10 ml-2"
                  data-testid="user-menu-trigger"
                >
                  <User className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline truncate max-w-[120px]">{user.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem disabled className="text-sm text-gray-500">
                  Connecté en tant que
                </DropdownMenuItem>
                <DropdownMenuItem disabled className="font-medium">
                  {user.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </nav>
  );
};
